import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Autocomplete,
  IconButton
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from '@mui/icons-material/Search';
import { fetchReport, handleError, formatDateForMySQL, formatCurrency, formatDate2, fetchProducts } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useReportDatePickerLocale } from '../../hooks/useReportDatePickerLocale';

const SellsByProductReport = () => {
  const { t } = useTranslation();
  const { datePickerFormat, dayjsPickerLocale } = useReportDatePickerLocale();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { setNavigation, resetNavigation, state } = useNavigationHelperStore();
  
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoadingProducts(true);
        const productsData = await fetchProducts();
        setProducts(productsData);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoadingProducts(false);
      }
    };
    loadProducts();
  }, []);

  useEffect(() => {
    if (state) {
      const { startDate: savedStartDate, endDate: savedEndDate, page: savedPage, selectedProduct: savedProduct } = state;
      if (savedStartDate) setStartDate(dayjs(savedStartDate));
      if (savedEndDate) setEndDate(dayjs(savedEndDate));
      if (savedPage) setPage(savedPage);
      if (savedProduct) setSelectedProduct(savedProduct);
      handleSearch();
    }
  }, []);

  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      startDate: startDate.toString(),
      endDate: endDate.toString(),
      page,
      data,
      selectedProduct
    });
  };

  const handleSearch = async () => {
    if (!selectedProduct) {
      snackbar.showSnackbar(t('reports.selectProductFirst'), "error");
      return;
    }

    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('SELLS_BY_PRODUCT', 1, {
        from: formattedStartDate,
        to: formattedEndDate,
        id_product: selectedProduct.id
      });
      
      setData(response);
      setPage(1);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePageChange = async (event, value) => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('SELLS_BY_PRODUCT', value, {
        from: formattedStartDate,
        to: formattedEndDate,
        id_product: selectedProduct.id
      });
      
      setData(response);
      setPage(value);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSellClick = (sell) => {
    saveCurrentState();
    navigate('/main/view-sell', { state: { id: sell.id } });
  };

  const getStatusFromBalance = (sell) => {
    if (sell.balance === 0) return 'PAID';
    if (sell.balance > 0 && sell.balance < sell.total) return 'PARTIALLY_PAID';
    return 'UNPAID';
  };

  const getStatusChip = (sell) => {
    if (sell.deleted === 1) {
      return <Chip label={t('common.cancelled')} color="default" size="small" />;
    }
    
    const status = getStatusFromBalance(sell);
    switch (status) {
      case 'PAID':
        return <Chip label={t('common.paid')} color="success" size="small" />;
      case 'PARTIALLY_PAID':
        return <Chip label={t('common.partialPayment')} color="warning" size="small" />;
      case 'UNPAID':
        return <Chip label={t('common.unpaid')} color="error" size="small" />;
      default:
        return <Chip label="-" size="small" />;
    }
  };

  const totals = useMemo(() => {
    if (!data?.content) return { total: 0, count: 0 };
    return {
      total: data.content.reduce((sum, sell) => sum + (sell.deleted === 1 ? 0 : sell.total), 0),
      count: data.content.filter(sell => sell.deleted !== 1).length
    };
  }, [data]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              options={products}
              getOptionLabel={(option) => option ? option.name : ''}
              value={selectedProduct}
              onChange={(event, newValue) => {
                setSelectedProduct(newValue);
              }}
              loading={isLoadingProducts}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('reports.selectProduct')}
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <DatePicker
                    label={t('common.from')}
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    format={datePickerFormat}
                    slotProps={{
                      textField: { fullWidth: true, variant: 'outlined' },
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={5}>
                  <DatePicker
                    label={t('common.to')}
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    format={datePickerFormat}
                    minDate={startDate}
                    slotProps={{
                      textField: { fullWidth: true, variant: 'outlined' },
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <IconButton 
                    onClick={handleSearch}
                    disabled={!selectedProduct || isLoading}
                    sx={{ 
                      p: 1, 
                      backgroundColor: 'primary.main', 
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'action.disabledBackground',
                      }
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Box>
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box mb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t('reports.sellsForProduct')} {selectedProduct?.name} ({data.total_results || data.content.length})
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                      <Typography variant="subtitle1">
                        {t('reports.sellsTotal')} {formatCurrency(totals.total)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
              
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table sx={{ minWidth: 350 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.date')}</TableCell>
                      <TableCell>{t('common.receipt')}</TableCell>
                      <TableCell>{t('reports.clientLabel')}</TableCell>
                      <TableCell>{t('common.user')}</TableCell>
                      <TableCell>{t('common.status')}</TableCell>
                      <TableCell align="right">{t('common.total')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.content.map((sell) => (
                      <TableRow 
                        key={sell.id}
                        hover
                        onClick={() => handleSellClick(sell)}
                        sx={{ 
                          '&:hover': { cursor: 'pointer' },
                          opacity: sell.deleted === 1 ? 0.5 : 1,
                          textDecoration: sell.deleted === 1 ? 'line-through' : 'none'
                        }}
                      >
                        <TableCell>{formatDate2(sell.date)}</TableCell>
                        <TableCell>{sell.receipt || '-'}</TableCell>
                        <TableCell>
                          {sell.owner?.name || (sell.pet?.name ? `(${sell.pet.name})` : '-')}
                        </TableCell>
                        <TableCell>{sell.user?.name || '-'}</TableCell>
                        <TableCell>{getStatusChip(sell)}</TableCell>
                        <TableCell align="right">{formatCurrency(sell.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noSellsForProduct')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('reports.selectProductAndDateRange')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default SellsByProductReport;